import React, { useState, useEffect } from 'react'
import axios from 'axios'
import moment from 'moment'
import { Box, Button, Typography, CircularProgress } from '@mui/material'
import { JsonView } from 'react-json-view-lite'
import { TIME_SOURCE, timeSourceTitle } from '../../../../utils/timeSource'

// Custom class names instead of importing the library's CSS. The library ships
// its styles as a `dist/index.css` that, when imported through JS, makes esbuild
// emit `application.css` into app/assets/builds — colliding with the SASS-compiled
// application.css. Styles for these classes live in app/assets/stylesheets/orders.scss.
const jsonViewerStyles = {
  container: 'rjv-container',
  basicChildStyle: 'rjv-basic-child',
  label: 'rjv-label',
  clickableLabel: 'rjv-clickable-label',
  nullValue: 'rjv-null',
  undefinedValue: 'rjv-undefined',
  numberValue: 'rjv-number',
  stringValue: 'rjv-string',
  booleanValue: 'rjv-boolean',
  otherValue: 'rjv-other',
  punctuation: 'rjv-punctuation',
  expandIcon: 'rjv-expand-icon',
  collapseIcon: 'rjv-collapse-icon',
  collapsedContent: 'rjv-collapsed-content',
  childFieldsContainer: 'rjv-child-fields'
}

interface InforDataResponse {
  data: Record<string, unknown> | null
  cached: boolean
  fetched_at?: string | null
  message?: string
}

interface InforDataSectionProps {
  basePath: string
  alreadyCached: boolean
  fetchedAt: string | null
}

const InforDataSection = ({ basePath, alreadyCached, fetchedAt }: InforDataSectionProps) => {
  const [response, setResponse] = useState<InforDataResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = (forceRefresh: boolean) => {
    setLoading(true)
    setError(null)
    const params = forceRefresh ? { force_refresh: 1 } : {}
    axios.get(`${basePath}/infor_data`, { params })
      .then(({ data }) => {
        setResponse(data)
        setLoading(false)
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Failed to fetch Infor data')
        setLoading(false)
      })
  }

  useEffect(() => {
    if (alreadyCached) fetchData(false)
  }, [alreadyCached])

  const copyToClipboard = () => {
    if (!response?.data) return
    navigator.clipboard.writeText(JSON.stringify(response.data, null, 2))
  }

  if (loading) {
    return <Box className='text-center py-3'><CircularProgress size={28} /></Box>
  }

  if (error) {
    return <Typography color='error'>{error}</Typography>
  }

  if (!response) {
    return (
      <Box>
        <Typography variant='body2' color='text.secondary' className='mb-2'>
          Click to fetch the original order payload from Infor. The response is cached after the first fetch.
        </Typography>
        <button type='button' className='btn btn-primary' onClick={() => fetchData(false)}>Fetch from Infor</button>
      </Box>
    )
  }

  if (!response.data) {
    return (
      <Box>
        <Typography variant='body2' className='mb-2'>
          {response.message || 'No data returned from Infor.'}
        </Typography>
        <Button variant='outlined' onClick={() => fetchData(true)} className='bw-button'>Try again</Button>
      </Box>
    )
  }

  const displayedFetchedAt = response.fetched_at || fetchedAt
  return (
    <Box>
      <div className='infor-data-toolbar'>
        <Typography variant='body2' color='text.secondary' title={timeSourceTitle(displayedFetchedAt, TIME_SOURCE.app)}>
          {displayedFetchedAt && `fetched ${moment(displayedFetchedAt).fromNow()}`}
        </Typography>
        <div>
          <Button variant='outlined' size='small' onClick={copyToClipboard} className='me-2 bw-button'>Copy JSON</Button>
          <Button variant='outlined' size='small' onClick={() => fetchData(true)} className='bw-button'>Refresh from Infor</Button>
        </div>
      </div>
      <Box className='infor-data-viewer mt-2'>
        <JsonView data={response.data} style={jsonViewerStyles} shouldExpandNode={(level) => level < 2} />
      </Box>
    </Box>
  )
}

export default InforDataSection
